import React, { useEffect, useMemo, useState } from 'react';
import { Camera, Check, Loader2, MapPin, Image as ImageIcon, Share2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useGroupContext } from '../lib/groups/context';
// Core replacements
import { getUserProfileCore, createContactCore, addContactToGroupCore, updateContactCoordinatesCore, uploadContactImageCore, setDefaultProfileImageCore, addContactImageCore, createRelationshipCore, createEventCore, getClaimedContactIdCore } from '@gv/core';
import { loadSharePreferences } from '../utils/sharePrefs';
import { formatContactText } from '../utils/contactShareUtils';
import { loadGoogleMapsPlaces, extractAddressParts, shortAddressLabel } from '../lib/googlePlaces';
import { reverseGeocodeConcurrent } from '../utils/reverseGeocode';

interface EmbeddedQuickAddContactProps {
    user: any;
    onSaved?: (contactId: string) => void;
}

type Step = 'phone' | 'name' | 'location' | 'photoAndReview' | 'share';

export default function EmbeddedQuickAddContact({ user, onSaved }: EmbeddedQuickAddContactProps) {
    const [step, setStep] = useState<Step>('phone');
    const [phone, setPhone] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [locationLabel, setLocationLabel] = useState('');
    const [locBusy, setLocBusy] = useState(false);
    const [locError, setLocError] = useState<string | null>(null);
    const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
    const [photoFile, setPhotoFile] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [createdContactId, setCreatedContactId] = useState<string | null>(null);
    // Removed About Me usage (deprecated)
    const [profileDisplayName, setProfileDisplayName] = useState<string>('');
    const [shareText, setShareText] = useState('');
    const [cardPhoto, setCardPhoto] = useState<File | null>(null);
    const [cardPreview, setCardPreview] = useState<string | null>(null);
    const [cardParsing, setCardParsing] = useState(false);
    const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
    const { activeGroupId } = useGroupContext();
    const [uploadingPhoto, setUploadingPhoto] = useState(false);

    const resetAll = () => {
        setStep('phone');
        setPhone('');
        setFirstName('');
        setLastName('');
        setLocationLabel('');
        setCoords(null);
        setPhotoFile(null); setPhotoPreview(null);
        setSaving(false);
        setCreatedContactId(null);
        setShareText('');
        setCardPhoto(null); setCardPreview(null); setCardParsing(false);
    };

    // Load display name only (About Me deprecated)
    useEffect(() => {
        (async () => {
            try {
                const prof = await getUserProfileCore(supabase as any, user.id);
                const display = (prof as any)?.display_name || `${(prof as any)?.first_name || ''} ${(prof as any)?.last_name || ''}`.trim();
                if (display) setProfileDisplayName(display); else setProfileDisplayName('');
            } catch { setProfileDisplayName(''); }
        })();
    }, [user?.id]);

    // Listen for the global scan trigger (from App FAB)
    useEffect(() => {
        const handler = () => {
            try {
                const el = document.getElementById('embedded-card-input') as HTMLInputElement | null;
                if (el) el.click();
            } catch { /* no-op */ }
        };
        window.addEventListener('gv:triggerCardScan' as any, handler as any);
        return () => window.removeEventListener('gv:triggerCardScan' as any, handler as any);
    }, []);

    // Keyboard detection for mobile footer
    useEffect(() => {
        const handleFocus = () => setIsKeyboardOpen(true);
        const handleBlur = () => setIsKeyboardOpen(false);

        // Track input focus/blur
        const inputs = document.querySelectorAll('input, textarea');
        inputs.forEach(input => {
            input.addEventListener('focus', handleFocus);
            input.addEventListener('blur', handleBlur);
        });

        // Use visual viewport if available (better mobile detection)
        if ('visualViewport' in window) {
            const viewport = window.visualViewport;
            const handleViewportChange = () => {
                const heightDiff = window.innerHeight - (viewport?.height || window.innerHeight);
                setIsKeyboardOpen(heightDiff > 150); // Threshold for keyboard height
            };
            viewport?.addEventListener('resize', handleViewportChange);
            return () => {
                inputs.forEach(input => {
                    input.removeEventListener('focus', handleFocus);
                    input.removeEventListener('blur', handleBlur);
                });
                viewport?.removeEventListener('resize', handleViewportChange);
            };
        }

        return () => {
            inputs.forEach(input => {
                input.removeEventListener('focus', handleFocus);
                input.removeEventListener('blur', handleBlur);
            });
        };
    }, []);

    // Format phone number as user types; base: assume no country code; if +1 present, group as US
    const formatPhone = (raw: string): string => {
        const hasPlus = raw.trim().startsWith('+');
        const digits = raw.replace(/\D/g, '');
        if (hasPlus && digits.startsWith('1')) {
            // +1 US formatting: +1 AAA BBB CCCC
            const nsn = digits.slice(1);
            if (nsn.length <= 3) return `+1 ${nsn}`.trim();
            if (nsn.length <= 6) return `+1 ${nsn.slice(0, 3)} ${nsn.slice(3)}`.trim();
            const core = `+1 ${nsn.slice(0, 3)} ${nsn.slice(3, 6)} ${nsn.slice(6, 10)}`.trim();
            const rest = nsn.slice(10);
            return rest ? `${core} ${rest}` : core;
        }
        // Local formatting (no explicit country code):
        // 1-3: as-is; 4-7: 3 4 grouping; 8+: 3 3 4 (+ rest)
        if (digits.length <= 3) return digits;
        if (digits.length <= 7) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
        const core = `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
        const rest = digits.slice(10);
        return rest ? `${core} ${rest}` : core;
    };

    const canGoNextFromPhone = useMemo(() => (phone.replace(/\D/g, '').length >= 7), [phone]);
    const canGoNextFromName = useMemo(() => !!firstName.trim() || !!lastName.trim(), [firstName, lastName]);

    const requestLocation = async () => {
        setLocError(null);
        if (!navigator.geolocation) { setLocError('Location not supported'); return; }
        setLocBusy(true);
        try {
            const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
            });
            setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
            const geo = await reverseGeocodeConcurrent(pos.coords.latitude, pos.coords.longitude, { timeoutMs: 6000 });
            if (geo.label) {
                setLocationLabel(geo.label);
            } else {
                setLocationLabel('');
                setLocError(geo.error || 'Reverse geocode failed');
            }
        } catch (e) {
            setLocError('Unable to get current location');
        } finally { setLocBusy(false); }
    };

    const onPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0] || null;
        setPhotoFile(f);
        if (f) {
            const reader = new FileReader();
            reader.onload = (ev) => setPhotoPreview(String(ev.target?.result || ''));
            reader.readAsDataURL(f);
        } else {
            setPhotoPreview(null);
        }
    };

    // Save a minimal contact, record coordinates if available, and prepare share text
    const saveContact = async () => {
        if (saving) return;
        if (createdContactId) { setStep('share'); return; }
        const raw = (phone || '').trim();
        const digits = raw.replace(/\D/g, '');
        const haveName = Boolean(firstName.trim() || lastName.trim());
        if (!haveName && !digits) {
            alert('Enter at least a name or a phone number');
            return;
        }

        // Validate user context - this is critical for contact ownership
        if (!user?.id) {
            alert('User authentication error. Please refresh the page and try again.');
            console.error('EmbeddedQuickAddContact: Missing user.id for contact creation');
            return;
        }

        setSaving(true);
        try {
            const now = new Date().toISOString();
            const first = (firstName || '').trim() || (digits ? 'Unknown' : 'New');
            const last = (lastName || '').trim() || 'Contact';
            const payload: any = {
                first_name: first,
                last_name: last,
                phone: digits || null,
                user_id: user.id,
                created_by: user.id,
                updated_by: user.id,
                created_at: now,
                updated_at: now,
            };

            // Duplicate guard (recent 5m)
            try {
                let dq = (supabase as any).from('contacts').select('id,created_at').eq('user_id', user.id).eq('first_name', first).eq('last_name', last).order('created_at', { ascending: false }).limit(1);
                if (digits) dq = dq.eq('phone', digits);
                const { data: dup } = await dq.maybeSingle();
                if (dup) {
                    const createdAt = new Date((dup as any).created_at).getTime();
                    if (!isNaN(createdAt) && Date.now() - createdAt < 5 * 60 * 1000) {
                        const dupId = (dup as any).id;
                        setCreatedContactId(dupId);
                        try {
                            const prefs = loadSharePreferences(activeGroupId);
                            const tmpl = { first_name: first, last_name: last, phone: digits || undefined } as any;
                            const txt = formatContactText(tmpl, prefs, { senderName: profileDisplayName || user?.email || 'me', meetingLocationLabel: locationLabel || null });
                            setShareText(txt);
                        } catch { setShareText(`Hey it's ${profileDisplayName || 'me'} — great meeting you!`); }
                        setStep('share');
                        setSaving(false);
                        return;
                    }
                }
            } catch (e) { console.warn('Duplicate guard failed', e); }

            const created = await createContactCore(supabase as any, payload);
            const newId = (created as any)?.id as string;
            try { window.dispatchEvent(new CustomEvent('gv:contactCreated', { detail: { id: newId } })); } catch { }
            setCreatedContactId(newId);

            // Link to active group if present
            try {
                if (activeGroupId && newId) {
                    await addContactToGroupCore(supabase as any, newId, activeGroupId);
                }
            } catch (e: any) {
                console.error('Failed to link contact to group due to RLS or permission error', e);
                alert(`Failed to link contact to group: ${e?.message || 'Unknown error'}\nThis needs a policy fix.`);
            }

            // Persist coordinates if captured
            try {
                if (coords && newId) {
                    await updateContactCoordinatesCore(supabase as any, newId, coords.lat, coords.lng);
                }
            } catch { /* ignore */ }

            // Photo upload (optional)
            let uploadedUrl: string | null = null;
            if (photoFile && newId) {
                setUploadingPhoto(true);
                try {
                    uploadedUrl = await uploadContactImageCore(supabase as any, photoFile, newId);
                    await setDefaultProfileImageCore(supabase as any, newId, uploadedUrl);
                    await addContactImageCore(supabase as any, newId, uploadedUrl, { source_type: 'manual' });
                } catch (e) { console.error('Photo upload failed', e); } finally { setUploadingPhoto(false); }
            }

            // Build share text
            let builtShare = '';
            try {
                const prefs = loadSharePreferences(activeGroupId);
                const templateData = { first_name: first, last_name: last, phone: digits || undefined } as any;
                builtShare = formatContactText(templateData, prefs, { senderName: profileDisplayName || user?.email || 'me', meetingLocationLabel: locationLabel || null });
                setShareText(builtShare);
            } catch {
                const base = `Hey, it's ${profileDisplayName || user?.email || 'me'}.`;
                const meet = locationLabel ? ` We met near ${locationLabel}.` : '';
                builtShare = `${base}${meet}\n\n${first} ${last}`.trim();
                setShareText(builtShare);
            }

            // Relationship (use actual display name instead of generic 'Me')
            try {
                const myContactId = await getClaimedContactIdCore(supabase as any, user.id);
                if (myContactId) {
                    const myName = profileDisplayName || user?.email || 'Me';
                    try {
                        await createRelationshipCore(
                            supabase as any,
                            newId,
                            {
                                relatedContactId: myContactId,
                                relatedName: myName,
                                relation: 'Met',
                                whereMet: locationLabel || undefined,
                                whenMet: new Date().toISOString(),
                                notes: locationLabel ? `Met near ${locationLabel}` : 'Met via quick add'
                            } as any
                        );
                    } catch (re) { console.error('Relationship create failed', re); }
                }
            } catch (e) { console.error('Claimed contact lookup failed', e); }

            // Meeting event
            try { await createEventCore(supabase as any, newId, { name: locationLabel ? `Met at ${locationLabel}` : 'Met', where: locationLabel || undefined, when: new Date().toISOString(), notes: builtShare.slice(0, 500), photoUrl: uploadedUrl || undefined } as any); } catch (ev) { console.error('Event creation failed', ev); }

            setStep('share');
            if (onSaved && newId) onSaved(newId);
        } catch (e) {
            console.error('Quick save failed', e);
            alert('Failed to save contact. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const doShare = async () => {
        try {
            // Prefer the OS share sheet so we can include a photo on mobile
            const fileToShare = photoFile || cardPhoto || null;
            const canUseWebShare = typeof navigator.share === 'function';
            const canShareFiles = !!(fileToShare && (navigator as any).canShare && (navigator as any).canShare({ files: [fileToShare] }));

            if (canUseWebShare) {
                try {
                    // Attempt share with text + (optional) file
                    if (canShareFiles) {
                        await (navigator as any).share({ text: shareText, files: [fileToShare] as any });
                    } else {
                        await (navigator as any).share({ text: shareText });
                    }
                    return;
                } catch (e: any) {
                    if (e?.name === 'AbortError') { return; }
                    // If file caused rejection, retry with text only
                    if (canShareFiles) {
                        try {
                            await (navigator as any).share({ text: shareText });
                            return;
                        } catch {/* fall through */ }
                    }
                }
            }

            // Fallback: open SMS composer (text only; most platforms don't support attaching files via sms: URL)
            const num = (phone || '').replace(/[^\d+]/g, '');
            if (num) {
                const smsUrl = `sms:${encodeURIComponent(num)}?&body=${encodeURIComponent(shareText)}`;
                window.location.href = smsUrl;
                try { await navigator.clipboard.writeText(shareText); } catch { /* ignore */ }
                return;
            }

            // Final fallback: copy to clipboard
            try { await navigator.clipboard.writeText(shareText); alert('Message copied. Paste into your SMS app.'); } catch { alert('Share not supported. Please copy and paste the message.'); }
        } catch {
            // No-op for embedded version
        }
    };

    // Business card parsing handlers
    const onCardChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0] || null;
        setCardPhoto(f);
        if (f) {
            const reader = new FileReader();
            reader.onload = (ev) => setCardPreview(String(ev.target?.result || ''));
            reader.readAsDataURL(f);
        } else {
            setCardPreview(null);
        }
    };

    const parseBusinessCard = async () => {
        if (!cardPhoto && !cardPreview) {
            // open file dialog
            document.getElementById('embedded-card-input')?.click();
            return;
        }
        setCardParsing(true);
        try {
            let dataUrl = cardPreview as string | null;
            if (!dataUrl && cardPhoto) {
                const reader = new FileReader();
                dataUrl = await new Promise<string>((resolve) => {
                    reader.onload = (ev) => resolve(String(ev.target?.result || ''));
                    reader.readAsDataURL(cardPhoto as File);
                });
            }
            if (!dataUrl) throw new Error('No card image');
            const [header, b64] = dataUrl.split(',');
            const mime = (header.match(/data:(.*);base64/)?.[1]) || 'image/jpeg';
            const { data, error } = await supabase.functions.invoke('gemini-vision-parse-card', {
                body: { imageBase64: b64, mimeType: mime, prompt: undefined }
            });
            if (error) throw new Error(error.message || 'Parse failed');
            const raw = String((data as any)?.response || '').trim();
            const cleaned = raw.replace(/```json\n?|```/g, '');
            const json = cleaned.substring(cleaned.indexOf('{'), cleaned.lastIndexOf('}') + 1);
            const parsed = JSON.parse(json);
            const name = String(parsed.first_name || '').trim() + ' ' + String(parsed.last_name || '').trim();
            setFirstName((parsed.first_name || '').trim());
            setLastName((parsed.last_name || '').trim());
            setPhone((parsed.phone || parsed.mobile || phone).toString());
            // If business present, we could set company later in full form; for quick flow we ignore
            setStep('location');
        } catch (e) {
            console.error('Card parse failed', e);
            alert('Could not parse business card. Try again with better lighting.');
        } finally {
            setCardParsing(false);
        }
    };

    return (
        <div className="max-w-xl mx-auto space-y-6 pb-28">{/* bottom padding so content not hidden behind sticky scan button */}
            {/* Hidden input for business card photo */}
            <input id="embedded-card-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={onCardChange} />

            {/* Step indicator */}
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400 overflow-x-auto whitespace-nowrap no-scrollbar py-1">
                <span className={`${step === 'phone' ? 'text-white' : ''}`}>Phone</span>
                <span className={`${step === 'name' ? 'text-white' : ''}`}>Name</span>
                <span className={`${step === 'location' ? 'text-white' : ''}`}>Location</span>
                <span className={`${step === 'photoAndReview' ? 'text-white' : ''}`}>Photo</span>
                <span className={`${step === 'share' ? 'text-white' : ''}`}>Share</span>
            </div>

            {/* Step: Phone */}
            {step === 'phone' && (
                <div className="bg-gray-900 border border-gray-700 p-4 sm:p-6">
                    <label className="block text-sm text-gray-300 mb-2">Phone Number</label>
                    <input
                        type="tel"
                        inputMode="tel"
                        pattern="[0-9+\-()\s]*"
                        value={phone}
                        onChange={(e) => setPhone(formatPhone(e.target.value))}
                        onPaste={(e) => {
                            try {
                                const text = (e.clipboardData || (window as any).clipboardData).getData('text');
                                if (text) {
                                    e.preventDefault();
                                    setPhone(formatPhone(text));
                                }
                            } catch { /* ignore */ }
                        }}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                        placeholder="e.g. +1 555 123 4567"
                    />
                    <div className="flex justify-end mt-4">
                        <button
                            onClick={() => setStep('name')}
                            disabled={!canGoNextFromPhone}
                            className="px-5 py-2 bg-white text-black disabled:opacity-40"
                        >Next</button>
                    </div>
                </div>
            )}

            {/* Step: Name */}
            {step === 'name' && (
                <div className="bg-gray-900 border border-gray-700 p-4 sm:p-6 space-y-3">
                    <div>
                        <label className="block text-sm text-gray-300 mb-1">First Name</label>
                        <input
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-300 mb-1">Last Name</label>
                        <input
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:justify-between mt-2">
                        <button onClick={() => setStep('phone')} className="px-4 py-2 border border-gray-700 text-gray-300">Back</button>
                        <button onClick={() => setStep('location')} disabled={!canGoNextFromName} className="px-5 py-2 bg-white text-black disabled:opacity-40">Next</button>
                    </div>
                </div>
            )}

            {/* Step: Location */}
            {step === 'location' && (
                <div className="bg-gray-900 border border-gray-700 p-4 sm:p-6">
                    {!locationLabel ? (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={requestLocation}
                                disabled={locBusy}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 border border-gray-700 text-white hover:bg-gray-700 disabled:opacity-50"
                            >
                                <MapPin className="w-4 h-4 text-blue-400" />
                                {locBusy ? 'Locating…' : 'Use my location'}
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between">
                            <div className="inline-flex items-center gap-2 max-w-[70%] px-3 py-2 bg-gray-800 border border-gray-700 text-gray-200 text-sm">
                                <MapPin className="w-4 h-4 text-blue-400" />
                                <span className="truncate">{locationLabel}</span>
                            </div>
                            <button
                                onClick={requestLocation}
                                className="text-sm text-blue-400 hover:underline"
                                disabled={locBusy}
                            >
                                {locBusy ? 'Updating…' : 'Update location'}
                            </button>
                        </div>
                    )}
                    {locError && <div className="text-xs text-red-400 mt-2">{locError}</div>}
                    <div className="flex flex-col sm:flex-row gap-2 sm:justify-between mt-4">
                        <button onClick={() => setStep('name')} className="px-4 py-2 border border-gray-700 text-gray-300">Back</button>
                        <button onClick={() => setStep('photoAndReview')} className="px-5 py-2 bg-white text-black">Next</button>
                    </div>
                </div>
            )}

            {/* Step: Photo + Review */}
            {step === 'photoAndReview' && (
                <div className="bg-gray-900 border border-gray-700 p-4 sm:p-6 space-y-4">
                    <div className="relative w-full">
                        <label className="block w-full aspect-square bg-black border border-gray-800 overflow-hidden flex items-center justify-center cursor-pointer group">
                            {photoPreview ? (
                                <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                            ) : (
                                <div className="flex flex-col items-center text-gray-500 group-hover:text-gray-300">
                                    <ImageIcon className="w-12 h-12 mb-2" />
                                    <div>Tap to add photo</div>
                                </div>
                            )}
                            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhotoChange} />
                            {photoPreview && (
                                <div className="absolute top-2 right-2 px-2 py-1 bg-black/60 text-white text-xs border border-gray-700 rounded">Change</div>
                            )}
                        </label>
                    </div>
                    <div className="space-y-1">
                        <div className="text-white text-lg font-medium">{`${firstName || ''} ${lastName || ''}`.trim() || 'New Contact'}</div>
                        <div className="text-gray-400">{phone || 'No phone provided'}</div>
                        {locationLabel && <div className="text-gray-500 text-sm">Near: {locationLabel}</div>}
                    </div>
                    <div className="mt-4 space-y-3">
                        <div className="flex justify-center">
                            <button onClick={() => setStep('location')} className="px-4 py-2 border border-gray-700 text-gray-300 w-full sm:w-auto">Back</button>
                        </div>
                        <div className="flex justify-center">
                            <button onClick={saveContact} disabled={saving} className="px-8 py-3 bg-green-600 text-white disabled:opacity-50 inline-flex items-center gap-2 w-full sm:w-auto justify-center">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                <span>Create</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Step: Share */}
            {step === 'share' && (
                <div className="bg-gray-900 border border-gray-700 p-4 sm:p-6 space-y-4">
                    <div className="text-white font-medium flex items-center gap-2"><Share2 className="w-4 h-4" /> Send SMS/Share</div>
                    <p className="text-sm text-gray-300">We'll open your device's share sheet. If image sharing isn't supported, the message is copied for you.</p>
                    <div className="bg-black border border-gray-800 p-3 text-gray-200 text-sm whitespace-pre-wrap">{shareText}</div>
                    <div className="mt-4 space-y-3">
                        <div className="flex justify-center">
                            <button onClick={() => setStep('photoAndReview')} className="px-4 py-2 border border-gray-700 text-gray-300 w-full sm:w-auto">Back</button>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 justify-center">
                            <button onClick={doShare} className="flex-1 px-5 py-2 bg-white text-black inline-flex items-center gap-2 justify-center sm:flex-none sm:w-auto">
                                <Share2 className="w-4 h-4" />
                                <span>Share</span>
                            </button>
                            <button onClick={() => { resetAll(); try { onSaved?.(createdContactId || ''); } catch { } }} className="flex-1 px-5 py-2 bg-gray-800 text-gray-200 border border-gray-700 hover:bg-gray-700 transition-colors justify-center inline-flex items-center gap-2 sm:flex-none sm:w-auto">Done</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sticky bottom Scan Business Card (moved per request) */}
            <div className="fixed bottom-0 inset-x-0 z-40">
                <div className="bg-gray-900/95 backdrop-blur-sm">
                    <div className="max-w-xl mx-auto px-4 py-3">
                        <button
                            type="button"
                            onClick={() => {
                                try {
                                    const el = document.getElementById('embedded-card-input') as HTMLInputElement | null;
                                    if (el) el.click();
                                } catch { /* no-op */ }
                            }}
                            className="w-full px-5 py-3 bg-black text-gray-200 hover:bg-gray-800 transition-colors border border-gray-700 inline-flex items-center justify-center gap-2"
                            title="Scan business card"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                <rect x="2" y="5" width="20" height="14" rx="2" ry="2"></rect>
                                <line x1="2" y1="10" x2="22" y2="10"></line>
                            </svg>
                            <span>Scan Business Card</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}